import { useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TIERS } from "@/lib/links";

export default function GiftSession() {
  const [tier, setTier] = useState(TIERS[1]);
  const [form, setForm] = useState({ buyer_name: "", buyer_email: "", recipient_name: "", recipient_email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const ok = form.buyer_name.length > 1 && /\S+@\S+\.\S+/.test(form.buyer_email) && form.recipient_name.length > 0;

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post("/gifts", { service_id: tier.id, ...form, origin_url: window.location.origin }, { timeout: 60000 });
      window.location.href = r.data.checkout_url;
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't start checkout — please call 310-422-0669"); setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="gift-page">
      <div className="mb-8 dg-reveal">
        <span className="dg-chip"><Gift className="w-3 h-3" /> Gift a session · never expires</span>
        <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight font-display">Give someone real tech help.</h1>
        <p className="mt-3 text-slate-300 max-w-2xl">Perfect for a parent, a friend or anyone who's been fighting their TV, Wi-Fi or phone. Pay once; they get a code and a printable gift card, then book whenever they're ready.</p>
      </div>

      <form onSubmit={submit} className="grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-5 space-y-3" data-testid="gift-tiers">
          <div className="dg-eyebrow">1 · Choose the session</div>
          {TIERS.map((t) => (
            <button key={t.id} type="button" onClick={() => setTier(t)} data-testid={`gift-tier-${t.id}`}
              className={`w-full text-left rounded-xl border p-4 transition-colors ${tier.id === t.id ? "border-[#FFD600] bg-[#FFD600]/10" : "border-slate-800 bg-slate-900/40 hover:border-[#FFD600]/60"}`}>
              <div className="flex items-center justify-between"><span className="font-display font-semibold">{t.title}</span><span className="text-xl font-extrabold text-[#FFD600] font-display">${t.price}</span></div>
              <div className="text-xs text-slate-400 mt-1">{t.short}</div>
            </button>
          ))}
        </div>
        <div className="lg:col-span-7 grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2 dg-eyebrow">2 · Who is it for?</div>
          <label className="block"><div className="dg-eyebrow mb-1">Their name</div><input className="dg-input" value={form.recipient_name} onChange={set("recipient_name")} data-testid="gift-recipient-name" /></label>
          <label className="block"><div className="dg-eyebrow mb-1">Their email (optional — we'll send it to them)</div><input className="dg-input" type="email" value={form.recipient_email} onChange={set("recipient_email")} data-testid="gift-recipient-email" /></label>
          <label className="block sm:col-span-2"><div className="dg-eyebrow mb-1">A short message (optional)</div><textarea className="dg-input" rows={2} value={form.message} onChange={set("message")} maxLength={400} placeholder="Happy birthday, Mom — no more fighting the remote!" data-testid="gift-message" /></label>
          <div className="sm:col-span-2 dg-eyebrow mt-2">3 · Your details</div>
          <label className="block"><div className="dg-eyebrow mb-1">Your name</div><input className="dg-input" value={form.buyer_name} onChange={set("buyer_name")} data-testid="gift-buyer-name" /></label>
          <label className="block"><div className="dg-eyebrow mb-1">Your email (receipt + gift card)</div><input className="dg-input" type="email" value={form.buyer_email} onChange={set("buyer_email")} data-testid="gift-buyer-email" /></label>
          <div className="sm:col-span-2 flex flex-wrap items-center gap-4 mt-2">
            <button type="submit" disabled={!ok || busy} className="dg-btn-primary rounded-full px-6 py-3 text-sm flex items-center gap-2 disabled:opacity-40" data-testid="gift-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />} {busy ? "Opening checkout…" : `Pay $${tier.price} & create gift`}
            </button>
            <span className="text-xs text-slate-500">Secure Stripe checkout. The code never expires. <Link to="/book" className="dg-link">Have a code? Book here.</Link></span>
          </div>
        </div>
      </form>
    </div>
  );
}
